import { ReactElement, useCallback, useMemo, useState } from "react";
import { ErrorContextType } from ".";

/**
 * Global error state used to show a well formed error from anywhere in the app.
 *
 */
const useErrorState: () => ErrorContextType = () => {
  const [errorDescription, setErrorDescription] = useState<
    string | ReactElement
  >();
  const [error, setLocalError] = useState<Error>();
  const [onDismissError, setOnDismissError] = useState<() => void>();
  const [showTitle, setShowTitle] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const errorMessage = error?.message;

  const setError = useCallback(
    (
      newError: Error,
      description: string | ReactElement,
      onDismiss?: () => void,
      showTitle?: boolean,
      showDetails?: boolean
    ) => {
      if (newError.message !== errorMessage) {
        if (showTitle !== undefined) setShowTitle(showTitle);
        if (showDetails !== undefined) setShowDetails(showDetails);
        setLocalError(newError);
        // see https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
        setOnDismissError(() => onDismiss);
        setErrorDescription(description);
      }
    },
    [errorMessage]
  );

  const handleDismissError = useCallback(() => {
    if (onDismissError) onDismissError();

    setShowTitle(false);
    setShowDetails(false);
    setOnDismissError(undefined);
    setErrorDescription(undefined);
    setLocalError(undefined);
  }, [onDismissError]);

  return useMemo(
    () => ({
      error,
      description: errorDescription,
      hasError: error !== undefined,
      showTitle,
      showDetails,
      setError,
      onDismissError: handleDismissError,
    }),
    [
      error,
      errorDescription,
      handleDismissError,
      setError,
      showDetails,
      showTitle,
    ]
  );
};

export default useErrorState;
